import React, { useState } from 'react';

import { Button } from '@epoch-ui/components/Button/button';
import { Icon } from '@epoch-ui/components/Icon';
import { Question } from 'types/question.types';
import { useToaster, ToasterVariant } from 'hooks/useToaster';
import { DeleteQuestionConfirmationDialog } from '@event-editor/components/EditQuestionSideSheet/DeleteQuestionConfirmationDialog';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';

import { useUpdateQuestionMutation } from '../mutations/useUpdateQuestionMutation';

interface DeleteQuestionButtonProps {
  eventId: number;
  existingQuestion?: Question;
  onClose: () => void;
  disabled?: boolean;
}

export const DeleteQuestionButton: React.FC<DeleteQuestionButtonProps> = ({
  eventId,
  existingQuestion,
  onClose,
  disabled
}) => {
  const { guestListTotals } = useEditableEvent();
  const confirmedGuests = guestListTotals?.confirmed ?? 0;
  const { showToast } = useToaster();
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);

  const updateQuestionMutation = useUpdateQuestionMutation(
    () => showToast('Failed to delete question', { variant: ToasterVariant.ERROR }),
    onClose
  );

  const handleDeleteClick = (): void => {
    // If there are confirmed guests and we're editing an existing question, show confirmation
    if (existingQuestion && confirmedGuests > 0) {
      setIsDeleteConfirmationOpen(true);
    } else {
      // Otherwise, delete immediately (or just close if it's a new question)
      handleDeleteQuestion();
    }
  };

  const handleDeleteQuestion = (): void => {
    if (existingQuestion) {
      updateQuestionMutation.mutate({
        eventId,
        question: { ...existingQuestion, isDeleted: true }
      });
    } else {
      // Just close the sheet if it's a new question
      onClose();
    }
  };

  return (
    <>
      <Button
        data-testid="delete-button"
        variant="destructive"
        size="sm"
        className="tw-bg-transparent tw-text-negative-foreground"
        type="button"
        onClick={handleDeleteClick}
        disabled={disabled || updateQuestionMutation.isPending}
      >
        <Icon name="trash-2" size={16} strokeWidth={2.5} />
        <span className="tw-ml-1">Delete</span>
      </Button>

      <DeleteQuestionConfirmationDialog
        open={isDeleteConfirmationOpen}
        onOpenChange={setIsDeleteConfirmationOpen}
        onConfirm={() => {
          setIsDeleteConfirmationOpen(false);
          handleDeleteQuestion();
        }}
      />
    </>
  );
};
